/** Geometry + project-state helpers for the construction-line offset
 *  feature (issue #29). An offset construction line carries an
 *  `offsetSource` ref pointing at another construction line; its
 *  `pathData` is regenerated from the source whenever the source
 *  changes. The user can't edit the geometry of an offset line
 *  directly — they edit the source, or change the offset distance
 *  via the properties panel / drag.
 *
 *  Keeps paperjs-offset confined to this module so the rest of the
 *  construction code can stay paper-agnostic where possible. */

import type paper from "paper";
import { PaperOffset } from "paperjs-offset";
import type {
  ConstructionLine,
  ConstructionPrimitive,
  Project,
} from "./Project";
import { projectConstructionLines } from "./Project";
import { rotatePathData } from "./constructionRotation";
import { applyCornerRounding } from "./constructionRounding";

/** Compute the offset path of `sourceLine` by `distance` mm.
 *  Positive distance = outward / right-of-direction for open paths,
 *  negative = inward / left. Returns the resulting SVG pathData or
 *  null when the source is unusable (empty / unparseable / produces
 *  a degenerate offset). */
export function computeOffsetPathData(
  sourcePathData: string,
  distance: number,
  scope: paper.PaperScope,
  options?: {
    /** Opt OUT of rounded corners (#423). paperjs-offset can only round
     *  or spike a corner — and a mitre spike is farther than `|d|` from
     *  the source, so the fold-back validator below shreds it — which
     *  makes the bisector offset the only way to keep corners sharp.
     *  It applies to an all-straight outline; anything with a curve in
     *  it falls through to the rounded engine, which is the only one
     *  that can offset curves at all. Default (and `true`) is rounded,
     *  matching what "expand uniformly" means geometrically. */
    roundedCorners?: boolean;
  },
): string | null {
  if (!sourcePathData) return null;
  if (!isFinite(distance) || distance === 0) {
    // A zero distance just re-emits the source path. The user-facing
    // path expects to ALWAYS get geometry back; refusing here would
    // make the derived line invisible at zero.
    return sourcePathData;
  }
  if (options?.roundedCorners === false) {
    const sharp = computeSharpPolyOffsetFromPathData(
      sourcePathData,
      distance,
      scope,
    );
    if (sharp) return sharp.pathData;
  }
  // paperjs-offset reads `paper.project.activeLayer` internally to
  // insert the result, so the active scope must be set before the
  // call. Activating here is idempotent if the caller already
  // activated it (which the rendering path does).
  scope.activate();
  let source: paper.Path | paper.CompoundPath | null = null;
  let result: paper.PathItem | null = null;
  try {
    const cp = new scope.CompoundPath({ pathData: sourcePathData });
    // PaperOffset wants a `Path`; reduce single-subpath compounds
    // to the single Path so the call typechecks and produces clean
    // geometry. Multi-subpath compounds (rare for construction
    // primitives) pass through as CompoundPath — paperjs-offset
    // handles those too at runtime even though the d.ts is narrow.
    if (cp.children && cp.children.length === 1) {
      const single = cp.children[0] as paper.Path;
      // Detach from the compound but keep inserted on the active
      // layer so paperjs-offset's internal `activeLayer` reads work.
      single.remove();
      scope.project.activeLayer.addChild(single);
      cp.remove();
      source = single;
    } else {
      source = cp;
    }
    // `round` join produces the most predictable result for our mix
    // of rectilinear + curved primitives; users rarely want mitred
    // spikes on inset offsets and `round` is what every other path
    // in the app uses (cf. dxfExporter / sceneToSvg).
    result = PaperOffset.offset(source as paper.Path, distance, {
      join: "round",
      cap: "butt",
    });
  } catch {
    if (source) source.remove();
    return null;
  }
  if (!result) {
    source.remove();
    return null;
  }
  // paperjs-offset's raw output can fold (self-crossing single
  // path) or fragment (disjoint pieces) at corners tighter than
  // |distance|. Both failure modes share one definition: the
  // "bad" portions of the raw output are NOT actually `|d|` away
  // from the source. Walk the raw path, drop the points whose
  // perpendicular distance to the source is wrong, and emit the
  // surviving runs as polylines. The result is the mathematically
  // valid inward offset — usually multi-piece (because parts of
  // the source genuinely have no valid `|d|`-offset region), but
  // every visible piece is correct.
  const cleaned = validateOffsetByDistanceToSource(
    result,
    source,
    distance,
    scope,
  );
  source.remove();
  result.remove();
  if (!cleaned) return null;
  const out = cleaned.pathData;
  cleaned.remove();
  return out && out.length > 0 ? out : null;
}

/** Walk a raw offset path point-by-point and keep only the runs
 *  whose perpendicular distance to the source path is within
 *  tolerance of `|distance|`. Mathematically:
 *
 *      validOffset = { p ∈ rawOffset : |p − nearest(source, p)| ≈ |d| }
 *
 *  This trims off the folded / fragmented / self-twisting
 *  portions that paperjs-offset emits around corners tighter
 *  than `|d|`, without throwing away the parts that ARE
 *  geometrically valid. Output is a polyline approximation —
 *  acceptable for a visual construction guide; bezier-fitting
 *  the valid runs would be a follow-up. */
function validateOffsetByDistanceToSource(
  raw: paper.PathItem,
  source: paper.PathItem,
  distance: number,
  scope: paper.PaperScope,
): paper.PathItem | null {
  const absDist = Math.abs(distance);
  // Tolerance scales with the requested distance — paperjs-offset
  // bezier-approximates inward arcs (rounded source corners → arcs
  // in the offset), and the approximation introduces ~5% radial
  // error in the worst case. We need to accept that as "valid"
  // while still rejecting fold-back / fragment artefacts (which
  // are typically off by 50%+ of |d|). 10% of |d| or 0.5 mm,
  // whichever is larger — comfortable margin above the
  // approximation error empirically observed (~0.15 mm worst-case
  // on a 3 mm offset of a 12-corner polyline).
  const tolerance = Math.max(0.5, absDist * 0.1);
  // Sampling step in project mm. 0.4 mm is dense enough to catch
  // short invalid sections (a folded-bezier corner ~1-2 mm wide)
  // while keeping the pass cheap on long paths (e.g., a 1-metre
  // perimeter path sampled in ~2500 points).
  const samplingStep = 0.4;
  const rawSubs: paper.Path[] =
    raw instanceof scope.CompoundPath
      ? (raw.children as paper.Path[])
      : [raw as paper.Path];
  const outPaths: paper.Path[] = [];
  for (const sub of rawSubs) {
    const len = sub.length;
    if (len < samplingStep * 3) continue;
    // Sample the subpath at uniform arc-length intervals + the
    // exact endpoint so we don't lose the trailing segment.
    type Sample = { point: paper.Point; valid: boolean };
    const samples: Sample[] = [];
    const steps = Math.max(2, Math.ceil(len / samplingStep));
    // A valid offset point is exactly `|d|` from its generator and never FARTHER
    // than `|d|` from the source (nearest ≤ generator). So a sample reading too
    // far flags genuinely-wrong geometry; reading too CLOSE is fine — it's the
    // concavity case where the nearest source point sits across a dent, not the
    // generator. Tracked separately so a wiggly-but-valid closed loop isn't
    // fragmented at its concavities (#683).
    let anyTooFar = false;
    for (let i = 0; i <= steps; i++) {
      const at = (i / steps) * len;
      const pt = sub.getPointAt(at);
      if (!pt) continue;
      const nearest = source.getNearestLocation(pt);
      // Defensive: getNearestLocation can return null on a
      // degenerate source (zero-length curve). Treat that as
      // "can't validate" → mark invalid so the run breaks here.
      const dist = nearest ? pt.getDistance(nearest.point) : Infinity;
      const valid = !!nearest && Math.abs(dist - absDist) <= tolerance;
      if (dist > absDist + tolerance) anyTooFar = true;
      samples.push({ point: pt, valid });
    }
    // A closed source's offset is itself a closed loop; when it doesn't read too
    // far anywhere AND doesn't self-cross, keep it CLOSED and use its exact
    // geometry — so an offset of a closed (e.g. freehand) shape stays a closed
    // shape (#683) instead of being reopened / fragmented into polylines. A
    // twisted-corner fold-back (paperjs-offset at `r ≈ |d|`, which the coarse
    // sampler can miss) self-intersects, so it falls through to the run-splitter.
    if (
      sub.closed &&
      samples.length > 3 &&
      !anyTooFar &&
      sub.getIntersections(sub).length === 0
    ) {
      outPaths.push(sub.clone({ insert: false }) as paper.Path);
      continue;
    }
    // Walk the sample list, extracting each contiguous run of
    // valid samples as its own polyline. Need at least 3 valid
    // points in a row to count as a "run" (skip noise from
    // single false-positive samples).
    let runStart = -1;
    for (let i = 0; i <= samples.length; i++) {
      const s = i < samples.length ? samples[i] : null;
      const isValid = !!s && s.valid;
      if (isValid && runStart < 0) {
        runStart = i;
      } else if (!isValid && runStart >= 0) {
        const runEnd = i - 1;
        if (runEnd - runStart >= 2) {
          const runPath = new scope.Path({ insert: false });
          for (let j = runStart; j <= runEnd; j++) {
            runPath.add(samples[j]!.point);
          }
          // Bezier-fit the validated sample polyline (issue #242).
          // The 0.4 mm sampler emits ~250 anchors on a 100 mm
          // rectangle perimeter; `simplify()` collapses straight
          // runs to 2 anchors and rounded paperjs-offset corners
          // (`join: "round"`) to a handful of beziers each, while
          // staying within the fit tolerance. 0.1 mm sits well
          // below both the sampler step (0.4 mm) and the validation
          // tolerance (`max(0.5 mm, 10% × |d|)`), so visible
          // deviation is below the existing approximation noise.
          // paper.Path.simplify returns `false` on degenerate input
          // (too few or collinear points it can't bezier-fit) — in
          // that case keep the un-simplified polyline so we never
          // lose geometry the validation pass already approved.
          runPath.simplify(0.1);
          outPaths.push(runPath);
        }
        runStart = -1;
      }
    }
  }
  if (outPaths.length === 0) return null;
  const out = new scope.CompoundPath({ insert: false });
  for (const p of outPaths) out.addChild(p);
  return out;
}

/** Heuristic offset for source lines that carry primitive metadata
 *  (issue #244). When the source is a parametric primitive whose
 *  offset geometry has a clean closed-form answer (a sharp
 *  rectangle's offset is a sharp rectangle of the same center with
 *  each axis ±2·distance, a circle's offset is a bigger / smaller
 *  circle, an ellipse's offset is roughly an ellipse with each
 *  half-axis ±distance, a pill's offset is the same pill with
 *  `width ± 2 * distance`, …), build the result directly from the
 *  primitive instead of running paperjs-offset over the rendered
 *  pathData. Two payoffs:
 *
 *  1. The output is a perfect parametric shape rather than the
 *     polyline / bezier approximation paperjs-offset would emit.
 *  2. The derived line can itself be a primitive — so it gets the
 *     same center-snap, dimension editing, and naming affordances
 *     the source has.
 *
 *  Returns null when the source has no primitive, the primitive
 *  kind isn't yet covered by the heuristic, or the result would
 *  be degenerate (zero / negative dimension). Callers fall back
 *  to `computeOffsetPathData` in those cases. */
export interface HeuristicOffsetResult {
  pathData: string;
  /** Set when the offset itself is a parametric primitive (the
   *  ellipse / pill / rectangle branches). Carries the offset
   *  shape's params so the derived line picks up center-snap,
   *  midpoint-snap, and primitive resize the way a fresh primitive
   *  would. */
  primitive?: ConstructionPrimitive;
  /** Set when the offset is a non-primitive shape that still has a
   *  recognised authoring kind (currently: `polygon`). Drives the
   *  properties panel's header label so a polygon-offset reads as
   *  "Polygon" rather than the un-labelled fallback. */
  kind?: "polyline" | "polygon" | "freehand";
  /** Per-corner radius metadata when the offset is a rounded
   *  rectangle. The derived line carries this so the rounded
   *  geometry survives subsequent edits via `deriveRoundedCorners`,
   *  and so the user-facing rounded-corner UI applies to the
   *  offset the same way it does to its source. `pathData` in the
   *  parent result is already the rounded form (applyCornerRounding
   *  output); these fields are stored on the derived line. */
  cornerRounding?: {
    sharpPathData: string;
    corners: Array<{ pathIndex: number; segmentIndex: number; radius: number }>;
  };
}

export interface HeuristicOffsetOptions {
  /** Mirrors `offsetSource.roundedCorners`. The heuristic treats
   *  `undefined` (the default) and `true` identically — outward
   *  offsets of sharp sources get arcs of radius `distance` at
   *  every corner. Pass `false` to opt out and keep sharp
   *  corners. */
  roundedCorners?: boolean;
}

export function computeHeuristicOffset(
  source: ConstructionLine,
  distance: number,
  scope: paper.PaperScope,
  options?: HeuristicOffsetOptions,
): HeuristicOffsetResult | null {
  const prim = source.primitive;
  if (!prim) {
    return computeHeuristicOffsetForNonPrimitive(
      source,
      distance,
      scope,
      options,
    );
  }
  if (prim.kind === "rectangle") {
    const newWidth = prim.width + 2 * distance;
    const newHeight = prim.height + 2 * distance;
    if (newWidth <= 0 || newHeight <= 0) return null;
    const offsetPrim: ConstructionPrimitive = {
      kind: "rectangle",
      x: prim.x - distance,
      y: prim.y - distance,
      width: newWidth,
      height: newHeight,
    };
    // Source has its own corner-rounding (PR #261): offset
    // inherits radii shifted by `distance`. Independent from the
    // `roundedCorners` opt-in below, which only fires on sharp
    // sources.
    if (source.cornerRounding) {
      const sharp = sharpRectanglePathData(offsetPrim);
      const offsetCorners = source.cornerRounding.corners
        .map((c) => ({
          pathIndex: c.pathIndex,
          segmentIndex: c.segmentIndex,
          radius: c.radius + distance,
        }))
        .filter((c) => c.radius > 0);
      const rounded = applyCornerRounding(sharp, offsetCorners, scope);
      let basePath = rounded ?? sharp;
      basePath = applySourceRotationToPath(basePath, source, scope);
      return {
        pathData: basePath,
        primitive: offsetPrim,
        cornerRounding:
          offsetCorners.length > 0
            ? { sharpPathData: sharp, corners: offsetCorners }
            : undefined,
      };
    }
    // Rounded outward corners are the DEFAULT for a sharp source
    // rectangle expanded outward — each new corner becomes an arc
    // of radius `distance` centred at the matching source corner,
    // matching the standard "round join" geometry of a uniform
    // outward expansion. The user can opt out via the "Round
    // corners" toggle, which sets `roundedCorners: false`. Skipped
    // for inward offsets entirely (rounded inward isn't
    // well-defined for a sharp source corner).
    if (options?.roundedCorners !== false && distance > 0) {
      const sharp = sharpRectanglePathData(offsetPrim);
      const offsetCorners = [0, 1, 2, 3].map((segmentIndex) => ({
        pathIndex: 0,
        segmentIndex,
        radius: distance,
      }));
      const rounded = applyCornerRounding(sharp, offsetCorners, scope);
      let basePath = rounded ?? sharp;
      basePath = applySourceRotationToPath(basePath, source, scope);
      return {
        pathData: basePath,
        primitive: offsetPrim,
        cornerRounding: { sharpPathData: sharp, corners: offsetCorners },
      };
    }
    return {
      pathData: heuristicPrimitivePathData(offsetPrim, source, scope),
      primitive: offsetPrim,
    };
  }
  if (prim.kind === "ellipse") {
    // Outward expansion (positive distance) grows each half-axis by
    // `distance`, which means each full axis grows by `2 * distance`
    // and the bbox top-left shifts by `-distance`.
    const newWidth = prim.width + 2 * distance;
    const newHeight = prim.height + 2 * distance;
    if (newWidth <= 0 || newHeight <= 0) return null;
    const offsetPrim: ConstructionPrimitive = {
      kind: "ellipse",
      x: prim.x - distance,
      y: prim.y - distance,
      width: newWidth,
      height: newHeight,
    };
    return {
      pathData: heuristicPrimitivePathData(offsetPrim, source, scope),
      primitive: offsetPrim,
    };
  }
  if (prim.kind === "pill") {
    // A pill is two semicircular caps of radius `width/2` joined by
    // a rectangular middle section running between (x1,y1)→(x2,y2).
    // Offsetting outward by `distance` moves the long sides out by
    // `distance` and grows each cap's radius by `distance` — both
    // effects collapse to "same axis endpoints (cap centres), short
    // axis grows by `2 * distance`". Endpoint orientation handles
    // any rotation directly, so no rotation field needs touching.
    const newWidth = prim.width + 2 * distance;
    if (newWidth <= 0) return null;
    const offsetPrim: ConstructionPrimitive = {
      kind: "pill",
      x1: prim.x1,
      y1: prim.y1,
      x2: prim.x2,
      y2: prim.y2,
      width: newWidth,
    };
    const pathData = heuristicPrimitivePathData(offsetPrim, source, scope);
    if (!pathData) return null;
    return { pathData, primitive: offsetPrim };
  }
  return null;
}

/** Heuristic offset for non-primitive sources — closed polygons
 *  AND open polylines (issue #244 follow-up). Every vertex shifts:
 *
 *  - Interior vertices (those with neighbours on both sides) along
 *    the interior-angle bisector, by `distance / cos(half-angle)`
 *    — same formula the polygon code has used since PR #258. The
 *    resulting edges stay parallel to the source edges at the
 *    requested perpendicular distance.
 *  - Open-polyline endpoints (vertex 0 and vertex N-1) shift along
 *    the perpendicular of their single adjacent edge by exactly
 *    `distance` — there's no second edge to bisect, so the
 *    bisector formula collapses to "perpendicular to the lone
 *    edge."
 *
 *  Returns null when:
 *  - The source isn't a closed polygon or an open polyline with
 *    straight, all-sharp-anchor segments.
 *  - Any interior vertex has a degenerate angle (two consecutive
 *    edges collinear and reversed) that would explode the
 *    bisector formula.
 *  - The result self-intersects (oversized inward offset through
 *    a polygon's inradius; paperjs-offset handles the fallback
 *    more gracefully).
 *  - The source carries corner-rounding metadata; that case is
 *    out of scope, same as the rectangle branch.
 *
 *  Sign convention: positive `distance` pushes the result to the
 *  LEFT of the source's traversal direction in y-down screen
 *  coords (paperjs-offset's convention for open paths). For
 *  closed sources, the visible "outward" still works because the
 *  signed area picks the winding direction and the helper flips
 *  the sign internally. Open polylines don't have an inside, so
 *  no flip is needed — positive really does mean left-of-direction
 *  there. */
function computeHeuristicOffsetForNonPrimitive(
  source: ConstructionLine,
  distance: number,
  scope: paper.PaperScope,
  options?: HeuristicOffsetOptions,
): HeuristicOffsetResult | null {
  if (source.kind !== "polygon" && source.kind !== "polyline") return null;
  if (source.cornerRounding) return null;
  const sharp = computeSharpPolyOffsetFromPathData(
    source.pathData,
    distance,
    scope,
  );
  if (!sharp) return null;
  const { pathData: sharpPath, closed } = sharp;
  // Rebuilt here because the corner list below indexes the offset
  // vertices; the count is the source's, which the sharp offset
  // preserves one-for-one.
  const offsetVerts = { length: sharp.vertexCount };
  const resultKind: "polygon" | "polyline" = closed ? "polygon" : "polyline";
  // Rounded corners default-on for outward expansion. For OPEN
  // polylines, the two end vertices have no second edge to round
  // against (they're missing an edge), so we skip them entirely
  // and only round interior vertices.
  if (options?.roundedCorners !== false && distance > 0) {
    const corners: Array<{
      pathIndex: number;
      segmentIndex: number;
      radius: number;
    }> = [];
    const firstRounded = closed ? 0 : 1;
    const lastRounded = closed
      ? offsetVerts.length - 1
      : offsetVerts.length - 2;
    for (let i = firstRounded; i <= lastRounded; i++) {
      corners.push({ pathIndex: 0, segmentIndex: i, radius: distance });
    }
    if (corners.length > 0) {
      const rounded = applyCornerRounding(sharpPath, corners, scope);
      return {
        pathData: rounded ?? sharpPath,
        kind: resultKind,
        cornerRounding: { sharpPathData: sharpPath, corners },
      };
    }
  }
  return { pathData: sharpPath, kind: resultKind };
}

/** Offset an all-straight, all-sharp polygon / polyline given only its
 *  pathData, keeping the corners SHARP: every vertex shifts along its
 *  interior-angle bisector so each edge stays parallel to its source at
 *  the requested perpendicular distance. This is the only offset in the
 *  codebase that produces sharp corners — paperjs-offset always rounds
 *  or spikes them, and its output is then rejected by the fold-back
 *  validator, which treats a mitre tip (farther than `|d|` from the
 *  source) as an artefact.
 *
 *  Sign convention matches `computeOffsetPathData`: positive `distance`
 *  expands a closed shape outward, and pushes an open path to the LEFT
 *  of its traversal direction in y-down space.
 *
 *  Returns null when the outline isn't a single straight-edged subpath
 *  with no bezier handles, when a vertex angle is degenerate, or when
 *  the result self-intersects (an inward offset past a local inradius)
 *  — callers fall back to the rounded offset in all of those cases.
 *  Extracted from the non-primitive heuristic (#244) so leather part /
 *  cut-out outlines, which are raw pathData with no construction line
 *  around them, can use the same corner treatment (#423). */
export function computeSharpPolyOffsetFromPathData(
  sourcePathData: string,
  distance: number,
  scope: paper.PaperScope,
): { pathData: string; closed: boolean; vertexCount: number } | null {
  const parsed = parseStraightPolyVertices(sourcePathData, scope);
  if (!parsed) return null;
  // Closed polylines (drawn with the "close" gesture in the polyline
  // sub-tool) and open polygons (legacy data) both happen — trust the
  // paper.js `closed` flag rather than any declared kind, so we offset
  // whatever the geometry actually is.
  const closed = parsed.closed;
  const offsetVerts = offsetPolyVertices(parsed.vertices, distance, closed);
  if (!offsetVerts) return null;
  scope.activate();
  const tmp = new scope.Path({ insert: false, closed });
  for (const v of offsetVerts) tmp.add(new scope.Point(v.x, v.y));
  const selfIntersects = tmp.getIntersections(tmp).length > 0;
  tmp.remove();
  if (selfIntersects) return null;
  const pathData = polyVerticesToPathData(offsetVerts, closed);
  if (!pathData) return null;
  return { pathData, closed, vertexCount: offsetVerts.length };
}

function parseStraightPolyVertices(
  pathData: string,
  scope: paper.PaperScope,
): {
  vertices: Array<{ x: number; y: number }>;
  closed: boolean;
} | null {
  if (!pathData) return null;
  scope.activate();
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  try {
    const subs: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    if (subs.length !== 1) return null;
    const sub = subs[0]!;
    const segs = sub.segments;
    // Need at least two vertices to define a single segment (the
    // open-polyline case); a closed source still needs three to
    // form a polygon.
    const minSegs = sub.closed ? 3 : 2;
    if (segs.length < minSegs) return null;
    // Every segment must be a sharp anchor — no bezier handles —
    // for the bisector formula to apply. A polyline that the user
    // edited via the bezier-cycle gains handles; punt to the
    // paperjs-offset fallback in that case.
    for (const s of segs) {
      if (!s.handleIn.isZero() || !s.handleOut.isZero()) return null;
    }
    return {
      vertices: segs.map((s) => ({ x: s.point.x, y: s.point.y })),
      closed: !!sub.closed,
    };
  } finally {
    cp.remove();
  }
}

/** Offset every vertex of a polygon / polyline. `closed` picks the
 *  topology:
 *
 *  - Closed (polygon): every vertex has a `prev` and `next` —
 *    bisector formula applies everywhere, and the signed-area sign
 *    flips so positive `distance` always means "push outward".
 *  - Open (polyline): vertex 0 has no `prev`, vertex N-1 has no
 *    `next`. Those endpoints shift along the LEFT-of-direction
 *    perpendicular of their single adjacent edge by exactly
 *    `distance` (paperjs-offset's open-path sign convention).
 *    Interior vertices still use the bisector. */
function offsetPolyVertices(
  verts: ReadonlyArray<{ x: number; y: number }>,
  distance: number,
  closed: boolean,
): Array<{ x: number; y: number }> | null {
  const n = verts.length;
  if (n < (closed ? 3 : 2)) return null;
  let d: number;
  if (closed) {
    // Shoelace signed area picks the source's winding direction so
    // positive `distance` reads as "outward" no matter how the user
    // drew the closed shape.
    let signedArea = 0;
    for (let i = 0; i < n; i++) {
      const a = verts[i]!;
      const b = verts[(i + 1) % n]!;
      signedArea += (b.x - a.x) * (b.y + a.y);
    }
    // In y-down screen coords:
    //   - signedArea < 0 ⇒ visual CW (interior is to the RIGHT of
    //     each edge): the right-of-direction normal already points
    //     outward.
    //   - signedArea > 0 ⇒ visual CCW: outward = -right-of-direction.
    const sign = signedArea >= 0 ? -1 : 1;
    d = distance * sign;
  } else {
    // No interior to flip toward — positive `distance` means
    // left-of-direction in y-down (paperjs-offset's open-path
    // convention). The `(ty, -tx)` normal used below already
    // points left-of-direction in y-down (despite the polygon
    // branch comments calling it "right of direction"; the polygon
    // path compensates via its signed-area sign flip), so no
    // adjustment to `distance` is needed.
    d = distance;
  }
  const out: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < n; i++) {
    const cur = verts[i]!;
    const hasPrev = closed || i > 0;
    const hasNext = closed || i < n - 1;
    // Single-edge endpoint case for open polylines: shift along
    // the perpendicular of the lone adjacent edge by exactly `d`.
    if (!hasPrev || !hasNext) {
      const otherIdx = hasPrev ? i - 1 : i + 1;
      const other = verts[otherIdx]!;
      const ex = hasPrev ? cur.x - other.x : other.x - cur.x;
      const ey = hasPrev ? cur.y - other.y : other.y - cur.y;
      const elen = Math.hypot(ex, ey);
      if (elen < 1e-9) return null;
      const tx = ex / elen;
      const ty = ey / elen;
      // Right-of-direction normal in y-down: (ty, -tx).
      out.push({ x: cur.x + ty * d, y: cur.y + -tx * d });
      continue;
    }
    const prev = verts[(i - 1 + n) % n]!;
    const next = verts[(i + 1) % n]!;
    const e1x = cur.x - prev.x;
    const e1y = cur.y - prev.y;
    const e1len = Math.hypot(e1x, e1y);
    if (e1len < 1e-9) return null;
    const e2x = next.x - cur.x;
    const e2y = next.y - cur.y;
    const e2len = Math.hypot(e2x, e2y);
    if (e2len < 1e-9) return null;
    const t1x = e1x / e1len;
    const t1y = e1y / e1len;
    const t2x = e2x / e2len;
    const t2y = e2y / e2len;
    const n1x = t1y;
    const n1y = -t1x;
    const n2x = t2y;
    const n2y = -t2x;
    const bx = n1x + n2x;
    const by = n1y + n2y;
    const bLen = Math.hypot(bx, by);
    if (bLen < 1e-9) return null;
    const bux = bx / bLen;
    const buy = by / bLen;
    const cosHalfAngle = bux * n1x + buy * n1y;
    if (Math.abs(cosHalfAngle) < 1e-6) return null;
    const mag = d / cosHalfAngle;
    out.push({ x: cur.x + bux * mag, y: cur.y + buy * mag });
  }
  // Edge-direction consistency: each offset edge should run in the
  // same direction as the matching source edge. A flipped tangent
  // means an inward offset crossed past a local inradius — bail and
  // let the paperjs-offset fallback handle the bowtie gracefully.
  const edgeCount = closed ? n : n - 1;
  for (let i = 0; i < edgeCount; i++) {
    const a = verts[i]!;
    const b = verts[(i + 1) % n]!;
    const oa = out[i]!;
    const ob = out[(i + 1) % n]!;
    const stx = b.x - a.x;
    const sty = b.y - a.y;
    const otx = ob.x - oa.x;
    const oty = ob.y - oa.y;
    if (stx * otx + sty * oty <= 0) return null;
  }
  return out;
}

function polyVerticesToPathData(
  verts: ReadonlyArray<{ x: number; y: number }>,
  closed: boolean,
): string {
  if (verts.length < (closed ? 3 : 2)) return "";
  const parts: string[] = [`M${verts[0]!.x} ${verts[0]!.y}`];
  for (let i = 1; i < verts.length; i++) {
    parts.push(`L${verts[i]!.x} ${verts[i]!.y}`);
  }
  if (closed) parts.push("Z");
  return parts.join(" ");
}

/** Build the rendered pathData for a heuristic-offset primitive,
 *  applying the source's rotation so the offset shares the source's
 *  visible orientation (the offset of a rotated ellipse is the
 *  expanded ellipse rotated by the same angle about the same pivot,
 *  since rotation is rigid and offset is rotation-invariant). */
function sharpRectanglePathData(
  rect: Extract<ConstructionPrimitive, { kind: "rectangle" }>,
): string {
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  return (
    `M${rect.x} ${rect.y} ` +
    `L${right} ${rect.y} ` +
    `L${right} ${bottom} ` +
    `L${rect.x} ${bottom} Z`
  );
}

function applySourceRotationToPath(
  pathData: string,
  source: ConstructionLine,
  scope: paper.PaperScope,
): string {
  const rot = source.rotation ?? 0;
  if (!source.rotationCenter || Math.abs(rot) < 1e-6) return pathData;
  return rotatePathData(
    pathData,
    source.rotationCenter.x,
    source.rotationCenter.y,
    rot,
    scope,
  );
}

function heuristicPrimitivePathData(
  offsetPrim: ConstructionPrimitive,
  source: ConstructionLine,
  scope: paper.PaperScope,
): string {
  scope.activate();
  let pathData = "";
  if (offsetPrim.kind === "rectangle") {
    pathData = sharpRectanglePathData(offsetPrim);
  } else if (offsetPrim.kind === "ellipse") {
    const tmp = new scope.Path.Ellipse(
      new scope.Rectangle(
        new scope.Point(offsetPrim.x, offsetPrim.y),
        new scope.Size(offsetPrim.width, offsetPrim.height),
      ),
    );
    pathData = tmp.pathData;
    tmp.remove();
  } else if (offsetPrim.kind === "pill") {
    // Inlined stadium path so the model layer doesn't reach into the
    // tools/ directory (CutoutTool's helper would create a cyclic
    // import via Scene). Same geometry as CutoutTool.makeSlotPathData:
    // two 180° arcs joined by parallel line segments.
    const dx = offsetPrim.x2 - offsetPrim.x1;
    const dy = offsetPrim.y2 - offsetPrim.y1;
    const length = Math.hypot(dx, dy);
    if (length < 1e-6) return "";
    const ux = dx / length;
    const uy = dy / length;
    const r = offsetPrim.width / 2;
    const px = -uy;
    const py = ux;
    const tl = {
      x: offsetPrim.x1 + px * r,
      y: offsetPrim.y1 + py * r,
    };
    const bl = {
      x: offsetPrim.x1 - px * r,
      y: offsetPrim.y1 - py * r,
    };
    const tr = {
      x: offsetPrim.x2 + px * r,
      y: offsetPrim.y2 + py * r,
    };
    const br = {
      x: offsetPrim.x2 - px * r,
      y: offsetPrim.y2 - py * r,
    };
    pathData =
      `M ${tl.x} ${tl.y} L ${tr.x} ${tr.y} ` +
      `A ${r} ${r} 0 0 0 ${br.x} ${br.y} ` +
      `L ${bl.x} ${bl.y} ` +
      `A ${r} ${r} 0 0 0 ${tl.x} ${tl.y} Z`;
  }
  return applySourceRotationToPath(pathData, source, scope);
}

/** Strip the offset-derived fields from a line (primitive / kind /
 *  cornerRounding / rotation / rotationCenter) without keeping
 *  `undefined` entries on the result. The snapshot helpers
 *  elsewhere in the codebase compare object identity & shallow
 *  equality, so leaving `undefined` fields around would mask
 *  genuine no-op cases. */
function stripDerivedPrimitiveFields(line: ConstructionLine): ConstructionLine {
  if (
    line.primitive === undefined &&
    line.kind === undefined &&
    line.cornerRounding === undefined &&
    line.rotation === undefined &&
    line.rotationCenter === undefined &&
    line.pivotPoint === undefined
  ) {
    return line;
  }
  const {
    primitive: _prim,
    kind: _kind,
    cornerRounding: _round,
    rotation: _rot,
    rotationCenter: _center,
    pivotPoint: _pivot,
    ...rest
  } = line;
  return rest;
}

/** If the sampled arc points describe a CIRCULAR arc, return the SVG
 *  pathData of the concentric arc offset by `distance` (same centre,
 *  radius ± distance, same angular span/direction). Returns null when
 *  the arc isn't circular (collinear points or samples not equidistant
 *  from the circumcentre) so the caller can fall back to the generic
 *  bezier offset. The offset side is picked from the mid-point tangent's
 *  radial normal using the same left-of-direction sign convention as the
 *  straight/preview path. Used for a circle's quadrants and a pill's
 *  semicircular caps (issue #681). */
function concentricArcOffsetPath(
  s: {
    p1x: number;
    p1y: number;
    p2x: number;
    p2y: number;
    midx: number;
    midy: number;
    s1x: number;
    s1y: number;
    s2x: number;
    s2y: number;
    midTanx: number;
    midTany: number;
  },
  distance: number,
  scope: paper.PaperScope,
): string | null {
  // Circumcentre of the triangle (p1, mid, p2).
  const ax = s.p1x,
    ay = s.p1y,
    bx = s.midx,
    by = s.midy,
    cx2 = s.p2x,
    cy2 = s.p2y;
  const d = 2 * (ax * (by - cy2) + bx * (cy2 - ay) + cx2 * (ay - by));
  if (Math.abs(d) < 1e-9) return null; // collinear — not an arc
  const a2 = ax * ax + ay * ay;
  const b2 = bx * bx + by * by;
  const c2 = cx2 * cx2 + cy2 * cy2;
  const cx = (a2 * (by - cy2) + b2 * (cy2 - ay) + c2 * (ay - by)) / d;
  const cy = (a2 * (cx2 - bx) + b2 * (ax - cx2) + c2 * (bx - ax)) / d;
  const r = Math.hypot(s.p1x - cx, s.p1y - cy);
  if (r < 1e-6) return null;
  // Confirm the whole curve — not just the 3 defining points — lies on
  // that circle, so a generic (non-circular) bezier falls through.
  const tol = Math.max(1e-3, r * 0.01);
  for (const [px, py] of [
    [s.s1x, s.s1y],
    [s.s2x, s.s2y],
    [s.p2x, s.p2y],
  ] as const) {
    if (Math.abs(Math.hypot(px - cx, py - cy) - r) > tol) return null;
  }
  // New radius: shift the arc's mid point along its (radial) normal by
  // `distance`, then read its distance from the centre — this picks
  // inward vs outward automatically and matches the preview's sign.
  const tl = Math.hypot(s.midTanx, s.midTany) || 1;
  const lnx = s.midTany / tl;
  const lny = -s.midTanx / tl;
  const newR = Math.hypot(
    s.midx + lnx * distance - cx,
    s.midy + lny * distance - cy,
  );
  if (newR < 1e-6) return null;
  const scaleTo = (px: number, py: number) => {
    const dist = Math.hypot(px - cx, py - cy) || 1;
    return new scope.Point(
      cx + ((px - cx) / dist) * newR,
      cy + ((py - cy) / dist) * newR,
    );
  };
  let arc: paper.Path | null = null;
  let data: string | null = null;
  try {
    arc = new scope.Path.Arc({
      from: scaleTo(s.p1x, s.p1y),
      through: scaleTo(s.midx, s.midy),
      to: scaleTo(s.p2x, s.p2y),
      insert: false,
    });
    data = arc.pathData || null;
  } catch {
    data = null;
  }
  if (arc) arc.remove();
  return data;
}

/** Single-segment offset (issue #681): produce ONE line parallel to a
 *  single edge of `source` — the curve at `segment.curveIndex` of
 *  subpath `segment.pathIndex` in `source.pathData` — offset by
 *  `distance` mm. A STRAIGHT edge yields a straight `M…L…`; a CURVED
 *  edge (one with bezier handles) is offset AS A CURVE via
 *  paperjs-offset so the parallel line keeps the bend instead of
 *  collapsing to a straight chord. The sign convention matches the
 *  whole-path offset preview (`ConstructionTool.refreshOffsetPreview`):
 *  positive `distance` moves to the LEFT of the edge's direction in
 *  y-down screen space (the `(tan.y, -tan.x)` normal). Returns null
 *  when the edge index is out of range (the source lost that edge) so
 *  the deriver keeps the last good geometry rather than blanking the
 *  line. */
export function computeSingleSegmentOffsetPath(
  source: ConstructionLine,
  distance: number,
  segment: { pathIndex: number; curveIndex: number },
  scope: paper.PaperScope,
): string | null {
  if (!source.pathData) return null;
  // No `applySourceRotationToPath` here (#1193): `source.pathData` is
  // already stored rotated, so the offset edge already is too. That helper
  // is for the primitive-based offsets, which start from the un-rotated
  // natural form.
  return computeSingleSegmentOffsetFromPathData(
    source.pathData,
    distance,
    segment,
    scope,
  );
}

/** Core of the single-edge offset, decoupled from `ConstructionLine` so a
 *  leather part outline (`piece.shapePathData`) can reuse the exact same
 *  geometry (#768). Takes a raw pathData string and returns the offset edge's
 *  pathData in the SAME coordinate space as the input path.
 *  Geometry & sign notes are documented on `computeSingleSegmentOffsetPath`. */
export function computeSingleSegmentOffsetFromPathData(
  sourcePathData: string,
  distance: number,
  segment: { pathIndex: number; curveIndex: number },
  scope: paper.PaperScope,
): string | null {
  scope.activate();
  let parsed: paper.CompoundPath | null = null;
  try {
    parsed = new scope.CompoundPath({
      pathData: sourcePathData,
      insert: false,
    });
  } catch {
    return null;
  }
  const subs: paper.Path[] =
    parsed.children && parsed.children.length > 0
      ? (parsed.children as paper.Path[])
      : [parsed as unknown as paper.Path];
  const sub = subs[segment.pathIndex];
  const curve = sub?.curves?.[segment.curveIndex] ?? null;
  if (!curve) {
    parsed.remove();
    return null;
  }
  // Capture the edge's geometry as plain numbers before dropping the
  // parsed source, then rebuild it in isolation below.
  const p1x = curve.point1.x;
  const p1y = curve.point1.y;
  const p2x = curve.point2.x;
  const p2y = curve.point2.y;
  const h1x = curve.handle1.x;
  const h1y = curve.handle1.y;
  const h2x = curve.handle2.x;
  const h2y = curve.handle2.y;
  const curved = curve.hasHandles();
  // Circular-arc heuristic (issue #681): a circular arc offsets EXACTLY
  // to a concentric arc — same centre, radius ± distance — which is
  // cleaner than the generic bezier offset. This covers a circle's
  // quadrants AND a pill's semicircular caps (and rounded-rect corners)
  // uniformly, since it detects the circle from the arc's own geometry
  // rather than the primitive kind. Sample the arc at a few parameters
  // while the parse is alive; the offset side is picked from the
  // mid-point tangent's radial normal.
  const mid = curved ? curve.getPointAtTime(0.5) : null;
  const midx = mid ? mid.x : 0;
  const midy = mid ? mid.y : 0;
  const s1 = curved ? curve.getPointAtTime(0.25) : null;
  const s1x = s1 ? s1.x : 0;
  const s1y = s1 ? s1.y : 0;
  const s2 = curved ? curve.getPointAtTime(0.75) : null;
  const s2x = s2 ? s2.x : 0;
  const s2y = s2 ? s2.y : 0;
  const midTan = curved ? curve.getTangentAtTime(0.5) : null;
  const midTanx = midTan ? midTan.x : 0;
  const midTany = midTan ? midTan.y : 0;
  parsed.remove();

  if (curved) {
    const arcPath = concentricArcOffsetPath(
      { p1x, p1y, p2x, p2y, midx, midy, s1x, s1y, s2x, s2y, midTanx, midTany },
      distance,
      scope,
    );
    if (arcPath) return arcPath;
  }

  if (curved) {
    // Curved edge: offset the lone bezier as an open path with
    // paperjs-offset (same lib / sign convention as the whole-shape
    // offset). paperjs-offset reads `activeLayer`, so the edge must be
    // inserted while it runs.
    let edge: paper.Path | null = null;
    let result: paper.PathItem | null = null;
    try {
      edge = new scope.Path({
        segments: [
          new scope.Segment(
            new scope.Point(p1x, p1y),
            undefined,
            new scope.Point(h1x, h1y),
          ),
          new scope.Segment(
            new scope.Point(p2x, p2y),
            new scope.Point(h2x, h2y),
            undefined,
          ),
        ],
        insert: true,
      });
      result = PaperOffset.offset(edge, distance, {
        join: "round",
        cap: "butt",
      });
    } catch {
      if (edge) edge.remove();
      if (result) result.remove();
      return null;
    }
    const data = result?.pathData ?? null;
    if (edge) edge.remove();
    if (result) result.remove();
    if (!data) return null;
    return data;
  }

  // Straight edge: exact perpendicular offset (cheap and predictable).
  const dx = p2x - p1x;
  const dy = p2y - p1y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) return null;
  // Left-of-direction normal in y-down space, matching the preview.
  const nx = dy / length;
  const ny = -dx / length;
  const ax = p1x + nx * distance;
  const ay = p1y + ny * distance;
  const bx = p2x + nx * distance;
  const by = p2y + ny * distance;
  const pathData = `M ${ax} ${ay} L ${bx} ${by}`;
  return pathData;
}

// ---------------------------------------------------------------------------
// Per-edge trimming (issue #116)
// ---------------------------------------------------------------------------

/** Sampling step in project mm for the source-edge attribution walk.
 *  Same value the offset validator uses, for the same reason: dense
 *  enough to resolve a short edge's run, cheap enough for a long
 *  perimeter. */
const SEGMENT_TRIM_STEP_MM = 0.4;

/** An edge of a source line, addressed the way `offsetSource.segment`
 *  and `offsetSource.excludedSegments` address one. */
export interface OffsetSourceSegment {
  pathIndex: number;
  curveIndex: number;
}

export function sameOffsetSegment(
  a: OffsetSourceSegment,
  b: OffsetSourceSegment,
): boolean {
  return a.pathIndex === b.pathIndex && a.curveIndex === b.curveIndex;
}

function parseSubpaths(
  pathData: string,
  scope: paper.PaperScope,
): { cp: paper.CompoundPath; subs: paper.Path[] } | null {
  if (!pathData) return null;
  scope.activate();
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subs: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  return { cp, subs };
}

/** Which edge of `sourcePathData` a world point sits nearest to.
 *  `paper.CompoundPath` has no `getNearestLocation`, so the scan walks
 *  the subpaths itself — which is also how the subpath index (the
 *  `pathIndex` half of the address) falls out. Returns null on an
 *  unparseable / empty source. */
export function nearestSourceSegment(
  sourcePathData: string,
  point: { x: number; y: number },
  scope: paper.PaperScope,
): (OffsetSourceSegment & { distance: number }) | null {
  const parsed = parseSubpaths(sourcePathData, scope);
  if (!parsed) return null;
  const pt = new scope.Point(point.x, point.y);
  let best: (OffsetSourceSegment & { distance: number }) | null = null;
  for (let pathIndex = 0; pathIndex < parsed.subs.length; pathIndex++) {
    const sub = parsed.subs[pathIndex]!;
    const loc = sub.getNearestLocation(pt);
    if (!loc || !loc.curve) continue;
    const distance = pt.getDistance(loc.point);
    if (!best || distance < best.distance) {
      best = { pathIndex, curveIndex: loc.curve.index, distance };
    }
  }
  parsed.cp.remove();
  return best;
}

/** How many edges the source has in total, so callers can tell when a
 *  set of excluded edges covers all of them. */
export function countSourceSegments(
  sourcePathData: string,
  scope: paper.PaperScope,
): number {
  const parsed = parseSubpaths(sourcePathData, scope);
  if (!parsed) return 0;
  let total = 0;
  for (const sub of parsed.subs) total += sub.curves?.length ?? 0;
  parsed.cp.remove();
  return total;
}

/** Split an offset path into the runs generated by a chosen subset of
 *  the source's edges.
 *
 *  Every point of an offset is `|distance|` from the source edge that
 *  generated it, so "which edge does this stretch follow" is answered
 *  by the nearest source location — the same attribution the offset
 *  validator already relies on. Walk the derived path, ask that
 *  question at each sample, and emit the contiguous runs `keep`
 *  accepted. Output is a polyline approximation, bezier-fitted by
 *  `simplify` exactly like `validateOffsetByDistanceToSource` — the
 *  same tradeoff, and only paid once the user has actually removed an
 *  edge (an offset with nothing excluded never reaches this code, so
 *  its geometry stays byte-identical to before #116).
 *
 *  Caveat inherited from nearest-point attribution: across a concave
 *  dent the nearest source point can belong to a neighbouring edge, so
 *  the cut lands a little off the true generating boundary there. For
 *  the convex outlines this gesture is aimed at (rectangles, polygons,
 *  rounded shapes) the attribution is exact.
 *
 *  Returns null when nothing survives. */
export function sliceOffsetBySourceSegments(
  derivedPathData: string,
  sourcePathData: string,
  keep: (segment: OffsetSourceSegment) => boolean,
  scope: paper.PaperScope,
): string | null {
  const derived = parseSubpaths(derivedPathData, scope);
  if (!derived) return null;
  const source = parseSubpaths(sourcePathData, scope);
  if (!source) {
    derived.cp.remove();
    return null;
  }
  const owner = (pt: paper.Point): OffsetSourceSegment | null => {
    let best: OffsetSourceSegment | null = null;
    let bestDist = Infinity;
    for (let pathIndex = 0; pathIndex < source.subs.length; pathIndex++) {
      const sub = source.subs[pathIndex]!;
      const loc = sub.getNearestLocation(pt);
      if (!loc || !loc.curve) continue;
      const d = pt.getDistance(loc.point);
      if (d < bestDist) {
        bestDist = d;
        best = { pathIndex, curveIndex: loc.curve.index };
      }
    }
    return best;
  };
  const outPaths: paper.Path[] = [];
  for (const sub of derived.subs) {
    const len = sub.length;
    if (len < SEGMENT_TRIM_STEP_MM * 3) continue;
    const steps = Math.max(2, Math.ceil(len / SEGMENT_TRIM_STEP_MM));
    const points: paper.Point[] = [];
    const kept: boolean[] = [];
    for (let i = 0; i <= steps; i++) {
      const pt = sub.getPointAt((i / steps) * len);
      if (!pt) continue;
      const seg = owner(pt);
      points.push(pt);
      // An unattributable sample (degenerate source curve) is dropped
      // rather than kept: better a slightly short run than geometry
      // that belongs to an edge the user removed.
      kept.push(!!seg && keep(seg));
    }
    if (points.length === 0) continue;
    if (kept.every(Boolean)) {
      // Nothing to cut out of this subpath — keep its exact geometry
      // (and its closed-ness) instead of round-tripping it through the
      // sampler.
      outPaths.push(sub.clone({ insert: false }) as paper.Path);
      continue;
    }
    if (!kept.some(Boolean)) continue;
    // Collect contiguous kept runs. On a CLOSED subpath the sample list
    // is a ring, so a run that spans the path's start point arrives as
    // two pieces (a head and a tail) that have to be rejoined — without
    // this, excluding one edge of a rectangle would leave the offset
    // broken at its seam as well as at the removed edge.
    const runs: paper.Point[][] = [];
    let current: paper.Point[] | null = null;
    for (let i = 0; i < points.length; i++) {
      if (kept[i]) {
        if (!current) {
          current = [];
          runs.push(current);
        }
        current.push(points[i]!);
      } else {
        current = null;
      }
    }
    if (sub.closed && runs.length > 1 && kept[0] && kept[points.length - 1]) {
      const head = runs.shift()!;
      runs[runs.length - 1]!.push(...head);
    }
    for (const run of runs) {
      if (run.length < 3) continue;
      const runPath = new scope.Path({ insert: false });
      for (const p of run) runPath.add(p);
      runPath.simplify(0.1);
      outPaths.push(runPath);
    }
  }
  derived.cp.remove();
  source.cp.remove();
  if (outPaths.length === 0) return null;
  const out = new scope.CompoundPath({ insert: false });
  for (const p of outPaths) out.addChild(p);
  const data = out.pathData;
  out.remove();
  return data && data.length > 0 ? data : null;
}

/** The offset geometry with every edge in `excluded` cut out of it —
 *  what a partially-deleted offset line renders as. */
export function trimOffsetToIncludedSegments(
  derivedPathData: string,
  sourcePathData: string,
  excluded: ReadonlyArray<OffsetSourceSegment>,
  scope: paper.PaperScope,
): string | null {
  if (excluded.length === 0) return derivedPathData;
  return sliceOffsetBySourceSegments(
    derivedPathData,
    sourcePathData,
    (seg) => !excluded.some((e) => sameOffsetSegment(e, seg)),
    scope,
  );
}

/** The complement: JUST the run one source edge generates. Drives the
 *  Delete sub-tool's hover highlight, so what lights up red is exactly
 *  what the tap removes. */
export function offsetRunForSourceSegment(
  derivedPathData: string,
  sourcePathData: string,
  segment: OffsetSourceSegment,
  scope: paper.PaperScope,
): string | null {
  return sliceOffsetBySourceSegments(
    derivedPathData,
    sourcePathData,
    (seg) => sameOffsetSegment(seg, segment),
    scope,
  );
}

/** The offset line and its source, when `constructionId` names an
 *  offset whose geometry can be trimmed per-edge. Null for a plain
 *  line, a mirror, an offset whose source is gone, or a single-edge
 *  offset (#681) — that one has exactly one stretch, so it is deleted
 *  whole rather than trimmed. */
function trimmableOffsetPair(
  project: Project,
  constructionId: string,
): { line: ConstructionLine; source: ConstructionLine } | null {
  const lines = projectConstructionLines(project);
  const line = lines.find((l) => l.id === constructionId);
  const link = line?.offsetSource;
  if (!line || !link || link.segment) return null;
  const source = lines.find((l) => l.id === link.sourceId);
  return source ? { line, source } : null;
}

/** Which source edge's stretch of an offset line a cursor position
 *  lands on (issue #116). Cheap — one nearest-location query per
 *  source subpath — so the Delete hover can call it on every pointer
 *  move and only build the run geometry when the answer changes. */
export function offsetSourceSegmentAt(
  project: Project,
  constructionId: string,
  point: { x: number; y: number },
  scope: paper.PaperScope,
): OffsetSourceSegment | null {
  const pair = trimmableOffsetPair(project, constructionId);
  if (!pair) return null;
  // The cursor sits `|distance|` off the edge that generated the
  // stretch under it, so the source edge nearest the CURSOR is that
  // generating edge — the same attribution the trim itself uses.
  const near = nearestSourceSegment(pair.source.pathData, point, scope);
  return near
    ? { pathIndex: near.pathIndex, curveIndex: near.curveIndex }
    : null;
}

/** The geometry of one source edge's stretch of an offset line — what
 *  the Delete hover paints red and what a tap removes. */
export function offsetSegmentRun(
  project: Project,
  constructionId: string,
  segment: OffsetSourceSegment,
  scope: paper.PaperScope,
): string | null {
  const pair = trimmableOffsetPair(project, constructionId);
  if (!pair) return null;
  return offsetRunForSourceSegment(
    pair.line.pathData,
    pair.source.pathData,
    segment,
    scope,
  );
}

/** Resolve a cursor position over an offset line into the source edge
 *  whose stretch it lands on, plus that stretch's geometry. The Delete
 *  sub-tool's hover paints the geometry and its tap stores the edge, so
 *  both come out of a single decision instead of two that can
 *  disagree. */
export function offsetSegmentPickAt(
  project: Project,
  constructionId: string,
  point: { x: number; y: number },
  scope: paper.PaperScope,
): { segment: OffsetSourceSegment; runPathData: string } | null {
  const segment = offsetSourceSegmentAt(project, constructionId, point, scope);
  if (!segment) return null;
  const runPathData = offsetSegmentRun(project, constructionId, segment, scope);
  return runPathData ? { segment, runPathData } : null;
}

/** Recompute every offset line's `pathData` from its source in the
 *  current project. Called from the commit pipeline so derived
 *  geometry stays in sync with whatever the user just did to the
 *  source. Lines whose source has been deleted are dropped (their
 *  back-ref no longer points at anything addressable).
 *
 *  When the source carries primitive metadata covered by the
 *  heuristic (issue #244), the derived line picks up a matching
 *  primitive + the source's rotation so it can participate in
 *  center-snap / primitive resize. When the source loses its
 *  primitive (becomes a polyline via a vertex edit), the heuristic
 *  fields are stripped from the derived line on the same commit.
 *
 *  Returns the input project unchanged when nothing needed
 *  updating, so callers can cheaply skip re-snapshotting. */
export function deriveConstructionOffsets(
  project: Project,
  scope: paper.PaperScope,
): Project {
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  let changed = false;
  const next: ConstructionLine[] = [];
  for (const line of lines) {
    const link = line.offsetSource;
    if (!link) {
      next.push(line);
      continue;
    }
    const source = byId.get(link.sourceId);
    if (!source) {
      // Source vanished — drop the orphan. Caller still owns the
      // commit so this prune lands in the history snapshot.
      changed = true;
      continue;
    }
    // Single-segment offset (issue #681): follow just ONE edge of the
    // source, not the whole outline. Skips the heuristic entirely and
    // emits a plain two-anchor line — so it flows through the
    // no-heuristic (path-only) update branch below.
    const segment = link.segment;
    // Edges the user removed with the Delete sub-tool (issue #116).
    // Meaningless on a single-edge offset, which has only the one edge
    // — deleting that deletes the line.
    const excluded =
      !segment && link.excludedSegments && link.excludedSegments.length > 0
        ? link.excludedSegments
        : null;
    const heuristic = segment
      ? null
      : computeHeuristicOffset(source, link.distance, scope, {
          roundedCorners: link.roundedCorners,
        });
    let derivedPath: string | null;
    if (segment) {
      derivedPath = computeSingleSegmentOffsetPath(
        source,
        link.distance,
        segment,
        scope,
      );
    } else if (heuristic) {
      derivedPath = heuristic.pathData;
    } else {
      derivedPath = computeOffsetPathData(
        source.pathData,
        link.distance,
        scope,
      );
    }
    // Trim the removed edges' runs out of whichever base the offset was
    // built from. The heuristic still runs first because its output is
    // the cleaner geometry to cut from — but a cut shape is no longer
    // that parametric primitive, so `useHeuristic` below drops the
    // primitive / kind / corner-rounding metadata along with it.
    const useHeuristic = heuristic !== null && !excluded;
    if (derivedPath !== null && excluded) {
      derivedPath = trimOffsetToIncludedSegments(
        derivedPath,
        source.pathData,
        excluded,
        scope,
      );
    }
    if (derivedPath === null) {
      // The offset produced no geometry (e.g. negative distance
      // larger than the source's inradius). Keep the existing
      // pathData so the layers panel still has a row — but flag
      // for now by leaving the line untouched.
      next.push(line);
      continue;
    }
    const samePath = derivedPath === line.pathData;
    const stripped = stripDerivedPrimitiveFields(line);
    let updated: ConstructionLine;
    if (useHeuristic && heuristic) {
      const baseRotation = source.rotation;
      const baseCenter = source.rotationCenter;
      const samePrim = heuristic.primitive
        ? line.primitive !== undefined &&
          primitiveEquals(line.primitive, heuristic.primitive)
        : line.primitive === undefined;
      const sameKind =
        (line.kind ?? undefined) === (heuristic.kind ?? undefined);
      const sameRound = cornerRoundingEquals(
        line.cornerRounding,
        heuristic.cornerRounding,
      );
      const sameRot = (line.rotation ?? 0) === (baseRotation ?? 0);
      const sameCenter = vec2Equals(line.rotationCenter, baseCenter);
      if (
        samePath &&
        samePrim &&
        sameKind &&
        sameRound &&
        sameRot &&
        sameCenter
      ) {
        next.push(line);
        continue;
      }
      const withPrim: ConstructionLine = {
        ...stripped,
        pathData: derivedPath,
      };
      if (heuristic.primitive) {
        withPrim.primitive = heuristic.primitive;
      }
      if (heuristic.kind) {
        withPrim.kind = heuristic.kind;
      }
      if (heuristic.cornerRounding) {
        withPrim.cornerRounding = heuristic.cornerRounding;
      }
      if (baseRotation !== undefined && Math.abs(baseRotation) > 1e-6) {
        withPrim.rotation = baseRotation;
      }
      if (baseCenter !== undefined) {
        withPrim.rotationCenter = baseCenter;
      }
      updated = withPrim;
    } else {
      // No heuristic — make sure the derived line doesn't carry
      // stale primitive metadata from a previous commit (e.g. the
      // source was a primitive and just dropped it). Path-only
      // update.
      const hadDerivedFields =
        line.primitive !== undefined ||
        line.kind !== undefined ||
        line.cornerRounding !== undefined ||
        line.rotation !== undefined ||
        line.rotationCenter !== undefined;
      if (samePath && !hadDerivedFields) {
        next.push(line);
        continue;
      }
      updated = { ...stripped, pathData: derivedPath };
    }
    // Update the lookup map so later iterations of this pass (a
    // chained offset-of-offset whose own source is earlier in the
    // array we just rewrote) re-derive from the FRESH pathData
    // rather than the pre-pass snapshot. Without this, chains
    // longer than one step lag a commit behind.
    byId.set(line.id, updated);
    next.push(updated);
    changed = true;
  }
  if (!changed) return project;
  return { ...project, constructionLines: next };
}

function primitiveEquals(
  a: ConstructionPrimitive,
  b: ConstructionPrimitive,
): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "ellipse" && b.kind === "ellipse") {
    return (
      near(a.x, b.x) &&
      near(a.y, b.y) &&
      near(a.width, b.width) &&
      near(a.height, b.height)
    );
  }
  if (a.kind === "rectangle" && b.kind === "rectangle") {
    return (
      near(a.x, b.x) &&
      near(a.y, b.y) &&
      near(a.width, b.width) &&
      near(a.height, b.height)
    );
  }
  if (a.kind === "pill" && b.kind === "pill") {
    return (
      near(a.x1, b.x1) &&
      near(a.y1, b.y1) &&
      near(a.x2, b.x2) &&
      near(a.y2, b.y2) &&
      near(a.width, b.width)
    );
  }
  if (a.kind === "line" && b.kind === "line") {
    return (
      near(a.x1, b.x1) &&
      near(a.y1, b.y1) &&
      near(a.x2, b.x2) &&
      near(a.y2, b.y2)
    );
  }
  return false;
}

function cornerRoundingEquals(
  a:
    | {
        sharpPathData: string;
        corners: ReadonlyArray<{
          pathIndex: number;
          segmentIndex: number;
          radius: number;
        }>;
      }
    | undefined,
  b:
    | {
        sharpPathData: string;
        corners: ReadonlyArray<{
          pathIndex: number;
          segmentIndex: number;
          radius: number;
        }>;
      }
    | undefined,
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  if (a.sharpPathData !== b.sharpPathData) return false;
  if (a.corners.length !== b.corners.length) return false;
  for (let i = 0; i < a.corners.length; i++) {
    const ac = a.corners[i]!;
    const bc = b.corners[i]!;
    if (ac.pathIndex !== bc.pathIndex) return false;
    if (ac.segmentIndex !== bc.segmentIndex) return false;
    if (!near(ac.radius, bc.radius)) return false;
  }
  return true;
}

function vec2Equals(
  a: { x: number; y: number } | undefined,
  b: { x: number; y: number } | undefined,
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return near(a.x, b.x) && near(a.y, b.y);
}

function near(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-6;
}

/** True when `line` is a derived offset (its geometry is owned by
 *  another line). Used by every edit handler to refuse direct
 *  geometry mutations. */
export function isOffsetDerived(line: ConstructionLine): boolean {
  return !!line.offsetSource;
}

/** Combined "lines that are visually moving with the drag" set,
 *  used by every drag site as the ghost-exclusion set passed to
 *  `findNearestSnapCandidate`. Unions:
 *
 *    1. `chainReachable` — chain-snap-reachable from `originIds`
 *       (already includes the origins). Caller computes this
 *       separately because it lives in the enforcement module.
 *    2. `reachableOffsets(project, chainReachable)` — every offset
 *       that follows any line in (1), recursively.
 *
 *  Without (2), an offset line's pre-drag model positions surface
 *  as ghost snap candidates while its rendered item is somewhere
 *  else — same root cause as #102 but reached via a different
 *  graph. */
export function ghostExclusionForDrag(
  project: Project,
  chainReachable: ReadonlySet<string>,
): Set<string> {
  const excluded = new Set<string>(chainReachable);
  const offsets = reachableOffsets(project, [...chainReachable]);
  for (const id of offsets) excluded.add(id);
  return excluded;
}

/** Forward closure of the offset-source graph from `originIds`:
 *  every construction line that follows (directly or transitively)
 *  any of the origins. Excludes the origins themselves so callers
 *  who already have their own line-id set can union the two
 *  cheaply.
 *
 *  Used by the drag pipeline so the live snap-candidate scan
 *  skips offset lines that are translating along with the drag —
 *  their PRE-drag model positions would otherwise surface as
 *  ghost candidates (#102, extended for #29 follow-up). */
export function reachableOffsets(
  project: Project,
  originIds: ReadonlyArray<string>,
): Set<string> {
  const out = new Set<string>();
  if (originIds.length === 0) return out;
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return out;
  // Build a reverse-adjacency map: for each `sourceId`, the lines
  // that follow it. A single BFS pass then walks every line whose
  // source is reachable from the origins.
  const childrenBySource = new Map<string, string[]>();
  for (const l of lines) {
    const s = l.offsetSource?.sourceId;
    if (!s) continue;
    if (!childrenBySource.has(s)) childrenBySource.set(s, []);
    childrenBySource.get(s)!.push(l.id);
  }
  const queue: string[] = [...originIds];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const kids = childrenBySource.get(cur);
    if (!kids) continue;
    for (const k of kids) {
      if (out.has(k)) continue;
      out.add(k);
      queue.push(k);
    }
  }
  return out;
}

/** Re-exported from `Project.ts` (#343). The walk is pure model data with no
 *  paper dependency, and it moved to the light module so `constructionMirror`
 *  can resolve an offset root without dragging `paperjs-offset` — and with it a
 *  canvas requirement at import time — into every module that touches mirrors.
 *  Kept exported here so the existing call sites don't all have to move. */
export { resolveOffsetRoot } from "./Project";
